/**
 * SafeFrame 1.1 Implementation
 * Provides secure ad container with controlled API access
 */

interface SafeFrameConfig {
  id: string;
  src?: string;
  html?: string;
  w: number;
  h: number;
  allowExpansion?: boolean;
  allowPushDown?: boolean;
  allowOverlay?: boolean;
  sandbox?: boolean;
  onReady?: (iframe: HTMLIFrameElement, doc: Document) => void;
}

interface SafeFrameAPI {
  expand: (config: { w?: number; h?: number; t?: number; l?: number; push?: boolean }) => void;
  collapse: () => void;
  geom: () => { win: any; self: any; exp: any };
  status: () => string;
  supports: (feature: string) => boolean;
}

export class SafeFrameHost {
  private containers: Map<string, SafeFrameContainer> = new Map();
  
  /**
   * Creates a SafeFrame container for an ad
   */
  createContainer(config: SafeFrameConfig): HTMLElement {
    const container = new SafeFrameContainer(config);
    this.containers.set(config.id, container);
    return container.render();
  }

  /**
   * Destroys a SafeFrame container
   */
  destroyContainer(id: string): void {
    const container = this.containers.get(id);
    if (container) {
      container.destroy();
      this.containers.delete(id);
    }
  }
}

class SafeFrameContainer {
  private iframe: HTMLIFrameElement;
  private config: SafeFrameConfig;
  private isExpanded: boolean = false;
  private originalDimensions: { w: number; h: number };

  constructor(config: SafeFrameConfig) {
    this.config = config;
    this.originalDimensions = { w: config.w, h: config.h };
    this.iframe = this.createSecureIframe();
    this.setupMessageHandling();
  }

  private createSecureIframe(): HTMLIFrameElement {
    const iframe = document.createElement('iframe');

    // Critical security settings — true SafeFrame is cross-origin.
    // Do NOT add allow-same-origin: that would let the creative reach window.parent
    // and defeats the entire point of SafeFrame isolation.
    iframe.setAttribute('sandbox', 'allow-scripts');
    iframe.setAttribute('scrolling', 'no');
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('marginwidth', '0');
    iframe.setAttribute('marginheight', '0');
    iframe.style.width = `${this.config.w}px`;
    iframe.style.height = `${this.config.h}px`;
    iframe.style.border = 'none';
    iframe.style.overflow = 'hidden';
    
    return iframe;
  }

  private setupMessageHandling(): void {
    window.addEventListener('message', (event) => {
      // After srcdoc/doc.write the iframe's contentWindow can be replaced,
      // so we can't rely on object identity. Match by the unique container id
      // baked into the message instead.
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data && data.sf && data.sf.id === this.config.id) {
          this.handleSafeFrameMessage(data);
        }
      } catch (e) {
        // Invalid message format - ignore
      }
    });
  }

  private handleSafeFrameMessage(data: any): void {
    switch (data.sf.cmd) {
      case 'expand':
        this.handleExpand(data.sf.data);
        break;
      case 'collapse':
        this.handleCollapse();
        break;
      case 'geom':
        this.sendGeometry();
        break;
      case 'status':
        this.sendStatus();
        break;
      default:
        console.warn('Unknown SafeFrame command:', data.sf.cmd);
    }
  }

  private handleExpand(expandData: any): void {
    if (!this.config.allowExpansion) {
      this.sendError('Expansion not allowed');
      return;
    }

    const newWidth = expandData.w || this.config.w;
    const newHeight = expandData.h || this.config.h;
    
    // Validate expansion limits (prevent full-screen takeover)
    const maxExpansion = { w: 500, h: 500 };
    if (newWidth > maxExpansion.w || newHeight > maxExpansion.h) {
      this.sendError('Expansion exceeds limits');
      return;
    }

    this.iframe.style.width = `${newWidth}px`;
    this.iframe.style.height = `${newHeight}px`;
    this.isExpanded = true;
    
    this.sendSuccess('expanded');
  }

  private handleCollapse(): void {
    this.iframe.style.width = `${this.originalDimensions.w}px`;
    this.iframe.style.height = `${this.originalDimensions.h}px`;
    this.isExpanded = false;
    
    this.sendSuccess('collapsed');
  }

  private sendGeometry(): void {
    const rect = this.iframe.getBoundingClientRect();
    const geometry = {
      win: {
        t: 0,
        l: 0,
        w: window.innerWidth,
        h: window.innerHeight
      },
      self: {
        t: rect.top,
        l: rect.left,
        w: rect.width,
        h: rect.height
      },
      exp: {
        t: rect.top,
        l: rect.left,
        w: rect.width,
        h: rect.height
      }
    };

    this.sendMessage('geom', geometry);
  }

  private sendStatus(): void {
    this.sendMessage('status', {
      expanded: this.isExpanded,
      supports: {
        expand: this.config.allowExpansion || false,
        overlay: this.config.allowOverlay || false,
        pushdown: this.config.allowPushDown || false
      }
    });
  }

  private sendMessage(cmd: string, data: any): void {
    const message = {
      sf: {
        id: this.config.id,
        cmd,
        data
      }
    };

    if (this.iframe.contentWindow) {
      this.iframe.contentWindow.postMessage(JSON.stringify(message), '*');
    }
  }

  private sendSuccess(status: string): void {
    this.sendMessage('success', { status });
  }

  private sendError(error: string): void {
    this.sendMessage('error', { error });
  }

  render(): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.style.position = 'relative';
    wrapper.style.width = `${this.config.w}px`;
    wrapper.style.height = `${this.config.h}px`;

    // Fire onReady at most once. Sandboxed cross-origin iframes won't expose
    // contentDocument to the parent, so onReady receives null for `doc`.
    let readyFired = false;
    this.iframe.addEventListener('load', () => {
      if (readyFired) return;
      readyFired = true;
      if (this.config.onReady) {
        let doc: Document | null = null;
        try { doc = this.iframe.contentDocument; } catch (e) { /* cross-origin */ }
        this.config.onReady(this.iframe, doc as Document);
      }
    });

    wrapper.appendChild(this.iframe);

    // Load ad content via srcdoc so we never have to doc.write into a live
    // iframe (which can re-fire onload and double-execute scripts).
    if (this.config.src) {
      this.iframe.src = this.config.src;
    } else if (this.config.html) {
      this.iframe.srcdoc = this.buildWrappedHTML(this.config.html);
    } else {
      this.iframe.src = 'about:blank';
    }

    return wrapper;
  }

  private buildWrappedHTML(html: string): string {
    const safeFrameAPI = this.generateSafeFrameAPI();
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><script>${safeFrameAPI}</script></head><body style="margin:0;padding:0;overflow:hidden">${html}</body></html>`;
  }

  private generateSafeFrameAPI(): string {
    const id = this.config.id;
    const supports = JSON.stringify({
      expand: this.config.allowExpansion || false,
      overlay: this.config.allowOverlay || false,
      pushdown: this.config.allowPushDown || false,
    });
    // NOTE: do NOT monkey-patch window.top / window.parent. The cross-origin
    // sandbox already prevents the creative from reaching the host page; the
    // override broke Saambaa (it depends on window.top for env detection) and
    // also broke $sf.ext.* itself, since those calls go through parent.postMessage.
    return `
      (function () {
        var ID = ${JSON.stringify(id)};
        var SUPPORTS = ${supports};
        function send(cmd, data) {
          try {
            window.parent.postMessage(JSON.stringify({ sf: { id: ID, cmd: cmd, data: data } }), '*');
          } catch (e) {}
        }
        var registered = null;
        window.$sf = {
          ver: '1-1-0',
          specVersion: '1-1-0',
          ext: {
            register: function (w, h, cb) { registered = { w: w, h: h, cb: cb }; },
            expand:   function (cfg) { send('expand', cfg); },
            collapse: function ()    { send('collapse'); },
            geom:     function ()    { send('geom'); return {}; },
            status:   function ()    { send('status'); return ''; },
            supports: function (f)   { return !!SUPPORTS[f]; },
            inViewPercentage: function () { return 100; },
            winHasFocus: function () { return true; }
          }
        };
      })();
    `;
  }

  destroy(): void {
    if (this.iframe.parentNode) {
      this.iframe.parentNode.removeChild(this.iframe);
    }
  }
}

// Global SafeFrame host instance
export const safeFrameHost = new SafeFrameHost();